/**
 * Utilitários para detecção de tipo de dispositivo, resolução e zoom
 */

export type DeviceType = 'mobile' | 'tablet' | 'tv' | 'desktop';

export interface DeviceInfo {
  type: DeviceType;
  width: number;
  height: number;
  pixelRatio: number;
  zoomLevel: number;
  isTV: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  tvPlatform?: string; // Plataforma de TV detectada (ex: 'fire-tv', 'android-tv', 'tizen', etc.)
}

/**
 * Obtém características do dispositivo de forma automática
 */
interface DeviceCapabilities {
  hasTouch: boolean;
  hasPointer: boolean;
  hasKeyboard: boolean;
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  isPC: boolean;
  isAndroid: boolean;
  isMobileDevice: boolean;
  hasTVKeywords: boolean;
}

function getDeviceCapabilities(): DeviceCapabilities {
  const userAgent = navigator.userAgent.toLowerCase();
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const hasPointer = 'onpointerdown' in window;
  // Verificar keyboard de forma segura (API pode não estar disponível ou tipada)
  const hasKeyboard = typeof (navigator as any).keyboard !== 'undefined';
  
  // Detectar palavras-chave genéricas de TV no User-Agent (não marcas específicas)
  // Padrões genéricos que indicam TV: "tv", "smart-tv", "android tv", etc.
  const tvKeywords = /\b(tv|smart[-_]?tv|android\s*tv|googletv|hbbtv|dtv|opera\s*tv)\b/i;
  const hasTVKeywords = tvKeywords.test(userAgent);
  
  // Detectar se é PC (Windows, Mac, Linux desktop - não Android)
  const isPC = /windows|macintosh|linux(?![^;]*android)|x11/i.test(userAgent);
  
  // Detectar Android
  const isAndroid = /android/i.test(userAgent);
  
  // Detectar se User-Agent indica mobile/tablet explicitamente
  const isMobileDevice = /\b(mobile|tablet|phone|iphone|ipod|blackberry|iemobile|opera\s*mini)\b/i.test(userAgent);
  
  return {
    hasTouch,
    hasPointer,
    hasKeyboard,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    pixelRatio: window.devicePixelRatio || 1,
    isPC,
    isAndroid,
    isMobileDevice,
    hasTVKeywords
  };
}

/**
 * Detecta se o dispositivo é uma TV baseado em heurísticas automáticas
 * IMPORTANTE: Deve ser restritivo para não detectar PCs como TVs
 * 
 * Abordagem automática usando:
 * 1. API moderna navigator.userAgentData (quando disponível) - fonte primária
 * 2. Características físicas e comportamentais (resolução, touch, pixel ratio)
 * 3. Análise genérica do User-Agent (palavras-chave, não listas de marcas)
 */
export function detectTV(): boolean {
  // ============================================
  // CAMADA 1: API Moderna navigator.userAgentData (mais confiável e automática)
  // ============================================
  if (typeof navigator !== 'undefined' && 'userAgentData' in navigator) {
    const uaData = (navigator as any).userAgentData;
    
    // Verificar se é TV usando a API moderna (detecção automática)
    if (uaData.platform === 'TV' || uaData.deviceClass === 'tv') {
      return true;
    }
    
    // Verificar brands automaticamente (qualquer brand que contenha "tv")
    if (uaData.brands && Array.isArray(uaData.brands)) {
      const hasTVBrand = uaData.brands.some((b: any) => {
        const brand = String(b.brand || '').toLowerCase();
        return brand.includes('tv') || brand.includes('television');
      });
      if (hasTVBrand) {
        return true;
      }
    }
    
    // Verificar getHighEntropyValues se disponível (informações mais detalhadas)
    if (typeof uaData.getHighEntropyValues === 'function') {
      // Esta é uma API assíncrona, mas podemos usar como fallback futuro
      // Por enquanto, continuamos com as outras camadas
    }
  }

  // ============================================
  // CAMADA 2: Análise automática do User-Agent (genérica, não hardcoded)
  // ============================================
  const userAgent = navigator.userAgent.toLowerCase();
  const caps = getDeviceCapabilities();
  
  // Detectar padrões genéricos de TV no User-Agent
  // Usa regex genéricos que capturam qualquer TV, não marcas específicas
  const tvPatterns = [
    /\b(smart[-_]?tv|smarttv)\b/i,           // Smart TV genérico
    /\b(android\s*tv|androidtv)\b/i,          // Android TV
    /\b(googletv|crkey)\b/i,                  // Google TV / Chromecast
    /\b(hbbtv|dtv)\b/i,                       // Padrões de TV digital
    /\b(opera\s*tv)\b/i,                      // Opera TV
    /\b(tizen|webos|netcast)\b/i,             // Sistemas operacionais de TV
    /\b(appletv|appletv\/)\b/i,               // Apple TV
    /\b(roku)\b/i,                            // Roku
    /\b(fire\s*tv|firetv|firestick|fire\s*stick|silk|fire\s*os|fireos)\b/i, // Fire TV (genérico)
    /\b(aft[a-z]{0,2})\b/i,                   // Códigos AFT (Amazon Fire TV) - qualquer variante
    /\b(tv\s*box|android\s*box)\b/i,          // TV Box genérico
  ];
  
  // Verificar se algum padrão genérico corresponde
  const isTVByUserAgent = tvPatterns.some(pattern => pattern.test(userAgent));
  
  // Se detectar por User-Agent com padrões genéricos, é TV
  if (isTVByUserAgent || caps.hasTVKeywords) {
    return true;
  }

  // ============================================
  // CAMADA 3: Detecção por características físicas (automática)
  // ============================================
  // Se for PC desktop (Windows/Mac/Linux), NÃO é TV
  if (caps.isPC) {
    return false;
  }

  // Heurísticas automáticas para TV:
  // 1. Android sem indicador de mobile/tablet + sem touch + resolução de TV
  // 2. Tela grande + sem touch + pixel ratio baixo + não é PC
  
  const isLargeScreen = caps.screenWidth >= 1920 && caps.screenHeight >= 1080;
  const isTVResolution = (caps.screenWidth >= 1280 && caps.screenHeight >= 720) || isLargeScreen;
  const hasLowPixelRatio = caps.pixelRatio <= 1.5;
  
  // Android sem mobile/tablet no User-Agent pode ser TV
  if (caps.isAndroid && !caps.isMobileDevice && !caps.hasTouch) {
    if (isTVResolution && hasLowPixelRatio) {
      return true;
    }
  }

  // Detecção genérica: dispositivo com características de TV
  // - Resolução típica de TV (>= 1280x720)
  // - Pixel ratio baixo (<= 1.5) - TVs geralmente têm pixel ratio 1
  // - SEM touch (TVs não têm touch)
  // - NÃO é PC
  // - NÃO é mobile/tablet explícito
  if (isTVResolution && hasLowPixelRatio && !caps.hasTouch && !caps.isPC && !caps.isMobileDevice) {
    return true;
  }

  return false;
}

/**
 * Detecta a plataforma de TV específica automaticamente (se for TV)
 * Extrai informações do User-Agent de forma genérica, sem hardcoding de marcas
 */
export function detectTVPlatform(): string | undefined {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Usar API moderna se disponível
  if (typeof navigator !== 'undefined' && 'userAgentData' in navigator) {
    const uaData = (navigator as any).userAgentData;
    if (uaData.platform) {
      const platform = String(uaData.platform).toLowerCase();
      if (platform.includes('tv') || platform.includes('television')) {
        // Extrair nome da plataforma automaticamente
        if (platform.includes('android')) return 'android-tv';
        if (platform.includes('tizen')) return 'tizen';
        if (platform.includes('webos')) return 'webos';
        if (platform.includes('roku')) return 'roku';
        return 'smart-tv';
      }
    }
  }
  
  // Extrair plataforma do User-Agent de forma genérica
  // Usa padrões que capturam qualquer variante, não apenas marcas conhecidas
  
  // Fire TV / Firestick (detecta qualquer variante AFT ou Fire)
  if (/\b(aft[a-z]{0,2}|fire\s*tv|firetv|firestick|fire\s*stick|silk|fire\s*os|fireos|amazon\s*fire\s*tv)\b/i.test(userAgent)) {
    return 'fire-tv';
  }
  
  // Android TV (qualquer variante)
  if (/\b(android\s*tv|androidtv|googletv|crkey)\b/i.test(userAgent)) {
    return 'android-tv';
  }
  
  // Tizen (Samsung, mas detecta automaticamente)
  if (/\btizen\b/i.test(userAgent)) {
    return 'tizen';
  }
  
  // WebOS (LG, mas detecta automaticamente)
  if (/\b(webos|netcast)\b/i.test(userAgent)) {
    return 'webos';
  }
  
  // Apple TV
  if (/\bappletv\b/i.test(userAgent)) {
    return 'appletv';
  }
  
  // Roku
  if (/\broku\b/i.test(userAgent)) {
    return 'roku';
  }
  
  // Chromecast
  if (/\b(crkey|chromecast)\b/i.test(userAgent)) {
    return 'chromecast';
  }
  
  // Smart TV genérica (qualquer TV que não se encaixe nos padrões acima)
  if (/\b(smart\s*tv|smarttv)\b/i.test(userAgent)) {
    return 'smart-tv';
  }
  
  // Se detectou como TV mas não identificou plataforma específica
  // Retorna undefined para indicar TV genérica
  return undefined;
}

/**
 * Detecta o tipo de dispositivo
 * IMPORTANTE: TV tem prioridade sobre mobile/tablet
 */
export function detectDeviceType(): DeviceType {
  // TV tem prioridade máxima
  if (detectTV()) {
    return 'tv';
  }

  const width = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();

  // Detectar mobile
  // Excluir TVs do padrão mobile
  const isMobile = (
    /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent) &&
    !detectTV() // Garantir que não é TV
  ) || (
    width <= 768 && 
    'ontouchstart' in window &&
    !detectTV() // Garantir que não é TV
  );

  if (isMobile) {
    return 'mobile';
  }

  // Detectar tablet
  // Excluir TVs do padrão tablet
  const isTablet = (
    /ipad|android(?!.*mobile)|tablet/i.test(userAgent) &&
    !detectTV() // Garantir que não é TV
  ) || (
    width > 768 && 
    width <= 1024 && 
    'ontouchstart' in window &&
    !detectTV() // Garantir que não é TV
  );

  if (isTablet) {
    return 'tablet';
  }

  return 'desktop';
}

/**
 * Calcula o nível de zoom detectado
 * Compara window.innerWidth com window.screen.width para detectar zoom
 * 
 * IMPORTANTE: TVs geralmente vêm com zoom de 150% (1.5x)
 */
export function detectZoomLevel(): number {
  const screenWidth = window.screen.width;
  const innerWidth = window.innerWidth;

  // Calcular zoom baseado na diferença entre screen e inner
  // Zoom de 150% = innerWidth é ~66.67% do screenWidth (1920 / 1280 = 1.5)
  // Zoom de 200% = innerWidth é 50% do screenWidth (1920 / 960 = 2.0)
  const zoomByInner = screenWidth / innerWidth;

  // PRIORIDADE: Detectar zoom de 150% primeiro (mais comum em TVs)
  // Valores entre 1.45 e 1.55 são considerados zoom de 150%
  if (zoomByInner >= 1.45 && zoomByInner <= 1.55) {
    return 1.5; // 150% - zoom padrão de TVs
  }

  // Se estiver próximo de 150% mas fora do range exato, ainda considerar 150%
  if (zoomByInner >= 1.4 && zoomByInner < 1.8) {
    // Mais próximo de 150% que de 200%
    return 1.5; // 150%
  }

  // Se detectar zoom de aproximadamente 200% (2x)
  if (zoomByInner >= 1.8 && zoomByInner <= 2.2) {
    return 2.0; // 200%
  }

  // Se detectar zoom de aproximadamente 125% (1.25x)
  if (zoomByInner >= 1.2 && zoomByInner < 1.4) {
    return 1.25; // 125%
  }

  // Retornar o zoom calculado ou 1.0 se não detectar zoom significativo
  return zoomByInner > 1.1 ? zoomByInner : 1.0;
}

/**
 * Obtém informações completas do dispositivo
 */
export function getDeviceInfo(): DeviceInfo {
  const type = detectDeviceType();
  const width = window.innerWidth;
  const height = window.innerHeight;
  const pixelRatio = window.devicePixelRatio || 1;
  const zoomLevel = detectZoomLevel();
  const isTV = type === 'tv';
  const tvPlatform = isTV ? detectTVPlatform() : undefined;

  return {
    type,
    width,
    height,
    pixelRatio,
    zoomLevel,
    isTV,
    isMobile: type === 'mobile',
    isTablet: type === 'tablet',
    isDesktop: type === 'desktop',
    tvPlatform
  };
}

// REMOVIDO: Todas as funções relacionadas a ajustes de TV foram removidas
// Apenas a detecção de dispositivo é mantida (mobile, tablet, tv, desktop)